import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useBlablaLinkProfileAvatar } from '../../features/game-profile/hooks/useBlablaLinkProfileAvatar.js';
import { preloadNikkeCatalog } from '../../features/nikke-catalog/hooks/useNikkeCatalog.js';
import { BlablaLinkProfileAvatar } from '../../features/game-profile/shared/BlablaLinkProfileAvatar.jsx';
import { MyInfoPage } from '../../features/my-info/shared/MyInfoPage.jsx';
import { useNavigationBadges } from '../../features/navigation-badges/hooks/useNavigationBadges.js';
import { DesktopPlatformUnionReview } from '../../features/platform-union-review/desktop/DesktopPlatformUnionReview.jsx';
import { DesktopRaidBridge } from '../../features/raid-bridge/desktop/DesktopRaidBridge.jsx';
import { DesktopRaidOverview } from '../../features/raid-overview/desktop/DesktopRaidOverview.jsx';
import { DesktopRaidPractice } from '../../features/raid-practice/desktop/DesktopRaidPractice.jsx';
import { DesktopRaidSettlement } from '../../features/raid-settlement/desktop/DesktopRaidSettlement.jsx';
import { RaidControlRoom } from '../../features/raid-control/shared/RaidControlRoom.jsx';
import { DesktopRaidSeason } from '../../features/raid-season/desktop/DesktopRaidSeason.jsx';
import { RaidStrategyPage } from '../../features/raid-tetris/shared/RaidStrategyPage.jsx';
import { RecommendedDeckBoard } from '../../features/recommended-decks/shared/RecommendedDeckBoard.jsx';
import { DesktopUnionMemberManagement } from '../../features/union-member-management/desktop/DesktopUnionMemberManagement.jsx';
import { DesktopUnionNotices } from '../../features/union-notices/desktop/DesktopUnionNotices.jsx';
import { SiteSupportBoard } from '../../features/union-support/shared/UnionSupportBoard.jsx';
import { canManageUnion as hasUnionManagementRole, canManageUnionMemberData } from '../../shared/domain/unionAccess.js';
import { DoroPartyWordmark } from '../../shared/ui/DoroPartyWordmark.jsx';
import { DororaidIcon } from '../../shared/ui/DororaidIcon.jsx';
import { SiteFooter } from '../../shared/ui/SiteFooter.jsx';
import { BroadcastEntryButton } from '../../features/broadcast/shared/BroadcastEntryButton.jsx';
import { SupabaseStatus } from '../../shared/ui/SupabaseStatus.jsx';

const ROUTES = new Set([
  'raid', 'control', 'practice', 'decks', 'tetris', 'notice', 'support', 'settlement', 'members', 'management',
  'my-info', 'my-nikke', 'account',
  'management/season', 'management/bridge', 'management/support', 'management/platform',
]);

const CORE_NAV_ITEMS = [
  { id: 'raid', label: '레이드 현황', icon: 'raid' },
  { id: 'control', label: '관제', icon: 'chat' },
  { id: 'practice', label: '모의전', icon: 'practice' },
  { id: 'decks', label: '추천덱', icon: 'decks' },
  { id: 'tetris', label: '테트리스', icon: 'tetris' },
  { id: 'notice', label: '공지', icon: 'notice' },
  { id: 'settlement', label: '결산', icon: 'settlement' },
];

const PAGE_META = {
  raid: { title: '레이드 현황', description: '보스 진행 상황과 실전 공격 기록을 확인합니다.' },
  control: { title: '유니온 채팅', description: '멤버 모의전, 공격 지시와 실시간 보스 체력을 함께 확인합니다.' },
  practice: { title: '모의전', description: '멤버별 모의전 슬롯과 예상 딜을 관리합니다.' },
  decks: { title: '추천덱', description: '저장한 스쿼드와 3타 조합을 관리합니다.' },
  tetris: { title: '테트리스', description: '보스별 남은 체력 추천과 단수별 계획표를 확인합니다.' },
  notice: { title: '공지', description: '유니온 운영 공지와 댓글을 확인합니다.' },
  support: { title: '사이트 문의', description: 'DoroParty 이용·계정·오류·데이터 관련 내용을 문의합니다.' },
  settlement: { title: '결산', description: '차수별 공격 기록과 기여도를 확인합니다.' },
  members: { title: '멤버', description: '유니온 멤버와 권한 정보를 관리합니다.' },
  management: { title: '관리', description: '레이드 차수와 데이터 수집 설정을 관리합니다.' },
  'my-info': { title: '내 정보', description: '프로필, 인증 정보와 계정 설정을 확인합니다.' },
  'my-nikke': { title: '내 니케', description: '보유 니케와 장비·오버로드 정보를 확인합니다.' },
  account: { title: '계정 관리', description: '로그인 계정과 데이터 삭제 설정을 관리합니다.' },
  'management/season': { title: '차수·보스 설정', description: '레이드 차수와 보스 정보를 설정합니다.', back: 'management' },
  'management/bridge': { title: '레이드 수집기', description: '실전 공격 기록을 직접 불러옵니다.', back: 'management' },
  'management/support': { title: '사이트 문의 관리', description: 'DoroParty 사이트 문의를 확인하고 답변합니다.', back: 'management' },
  'management/platform': { title: '플랫폼 관리', description: '유니온 등록 심사와 계정·식별자 밴을 관리합니다.', back: 'management' },
};

function readRouteFromHash() {
  if (typeof window === 'undefined') return 'raid';
  const route = window.location.hash.replace(/^#\/?/, '').split('?')[0].replace(/\/+$/, '');
  return ROUTES.has(route) ? route : 'raid';
}

function readSidebarCollapsed() {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem('dororaid:desktop-sidebar-collapsed') === '1';
}

function DesktopFeaturePlaceholder({ title, description }) {
  return <section className="desktop-feature-placeholder" role="status"><div aria-hidden="true">◇</div><span>PAGE READY</span><h2>{title}</h2><p>{description}</p></section>;
}

function AlertBadge({ value, className = 'nav-alert-badge' }) {
  if (!value) return null;
  const label = value === '!' ? '새 알림' : `${value}개 알림`;
  return <span className={className} aria-label={label}>{value}</span>;
}

function IconBadgeAnchor({ icon, badge, size }) {
  return <span className="nav-icon-badge-anchor"><DororaidIcon name={icon} size={size} /><AlertBadge value={badge} /></span>;
}

function DesktopMenu({ items, onNavigate }) {
  return <section className="desktop-menu-grid" aria-label="관리 세부 메뉴">{items.map((item) => <button key={item.id} type="button" onClick={() => onNavigate(item.id)}><IconBadgeAnchor icon={item.icon} badge={item.badge} /><div><strong>{item.title}</strong><p>{item.description}</p></div><b aria-hidden="true"><DororaidIcon name="chevronRight" size={18} /></b></button>)}</section>;
}

function ProfileEntryButton({ nickname, email, avatarUrl, onClick }) {
  const label = String(nickname || email || '내 정보').trim();
  return <button className="desktop-profile-entry" type="button" onClick={onClick} title="내 정보 열기"><BlablaLinkProfileAvatar className="profile-entry-avatar" nickname={label} avatarUrl={avatarUrl} /><div><strong>{label}</strong><small>내 정보</small></div></button>;
}

function DesktopSidebarNavigation({ sections, activePage, onNavigate }) {
  return <nav className="desktop-sidebar-nav" aria-label="주요 메뉴">{sections.map((section) => <section className="desktop-sidebar-section" key={section.id} aria-labelledby={`desktop-sidebar-${section.id}`}><h2 id={`desktop-sidebar-${section.id}`}>{section.label}</h2><div>{section.items.map((item) => <button className={activePage === item.id ? 'active' : ''} key={item.id} type="button" title={item.label} aria-current={activePage === item.id ? 'page' : undefined} onClick={() => onNavigate(item.id)}><IconBadgeAnchor icon={item.icon} badge={item.badge} /><strong>{item.label}</strong></button>)}</div></section>)}</nav>;
}

export function DesktopShell({ user, onSignOut, entry }) {
  useEffect(() => {
    const run = () => preloadNikkeCatalog().catch(() => {});
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(run, { timeout: 1200 });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timerId = window.setTimeout(run, 0);
    return () => window.clearTimeout(timerId);
  }, []);
  const unionId = String(entry?.union?.id || '');
  const unionName = entry?.union?.name || entry?.profile?.guild_name || '내 유니온';
  const nickname = entry?.profile?.nickname || '';
  const profileAvatar = useBlablaLinkProfileAvatar();
  const [page, setPage] = useState(readRouteFromHash);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [controlRouteEntering, setControlRouteEntering] = useState(false);
  const controlEnterFrameRef = useRef(0);
  const controlReleaseFrameRef = useRef(0);
  const internalHashPageRef = useRef('');
  const role = String(entry?.membership?.role || '');
  const canManageUnion = hasUnionManagementRole(role);
  const canManageMemberData = canManageUnionMemberData(role);
  const canManagePlatform = Boolean(entry?.isPlatformAdmin);
  const canOpenManagement = canManageUnion || canManagePlatform;
  const badges = useNavigationBadges({ page, unionId, userId: String(user?.id || ''), canManagePlatform });
  const meta = PAGE_META[page] || PAGE_META.raid;

  const navigationSections = useMemo(() => {
    const coreItems = CORE_NAV_ITEMS.map((item) => item.id === 'notice'
      ? { ...item, badge: badges.noticeUnread ? '!' : '' }
      : item.id === 'control' ? { ...item, badge: badges.raidControlUnread ? '!' : '' } : item);
    const sections = [{ id: 'raid', label: '레이드', items: coreItems.slice(0, 5) }, { id: 'records', label: '기록', items: coreItems.slice(5) }];
    const managementItems = [
      ...(canManageUnion ? [{ id: 'members', label: '멤버', icon: 'members' }] : []),
      ...(canOpenManagement ? [{ id: 'management', label: '관리', icon: 'management', badge: badges.managementCount || '' }] : []),
    ];
    if (managementItems.length) sections.push({ id: 'management', label: '관리', items: managementItems });
    return sections;
  }, [badges.managementCount, badges.noticeUnread, badges.raidControlUnread, canManageUnion, canOpenManagement]);

  const commitNavigation = useCallback((normalized, syncHash = true) => {
    setPage(normalized);
    if (syncHash && window.location.hash !== `#/${normalized}`) {
      internalHashPageRef.current = normalized;
      window.location.hash = `/${normalized}`;
    }
  }, []);

  const beginControlTransition = useCallback((syncHash = true) => {
    if (controlEnterFrameRef.current) window.cancelAnimationFrame(controlEnterFrameRef.current);
    setControlRouteEntering(true);
    controlEnterFrameRef.current = window.requestAnimationFrame(() => {
      controlEnterFrameRef.current = 0;
      commitNavigation('control', syncHash);
    });
  }, [commitNavigation]);

  const navigate = useCallback((nextPage) => {
    const normalized = ROUTES.has(nextPage) ? nextPage : 'raid';
    if (normalized === page) return;
    if (normalized === 'control') {
      beginControlTransition(true);
      return;
    }
    setControlRouteEntering(false);
    commitNavigation(normalized, true);
  }, [beginControlTransition, commitNavigation, page]);

  useEffect(() => {
    const handleHashChange = () => {
      const nextPage = readRouteFromHash();
      if (internalHashPageRef.current === nextPage) {
        internalHashPageRef.current = '';
        return;
      }
      if (nextPage === page) return;
      if (nextPage === 'control') beginControlTransition(false);
      else {
        setControlRouteEntering(false);
        commitNavigation(nextPage, false);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [beginControlTransition, commitNavigation, page]);

  useLayoutEffect(() => {
    if (!controlRouteEntering || page !== 'control') return undefined;
    if (controlReleaseFrameRef.current) window.cancelAnimationFrame(controlReleaseFrameRef.current);
    controlReleaseFrameRef.current = window.requestAnimationFrame(() => {
      controlReleaseFrameRef.current = 0;
      setControlRouteEntering(false);
    });
    return () => {
      if (controlReleaseFrameRef.current) window.cancelAnimationFrame(controlReleaseFrameRef.current);
    };
  }, [controlRouteEntering, page]);

  useEffect(() => () => {
    if (controlEnterFrameRef.current) window.cancelAnimationFrame(controlEnterFrameRef.current);
    if (controlReleaseFrameRef.current) window.cancelAnimationFrame(controlReleaseFrameRef.current);
  }, []);

  useEffect(() => { window.localStorage.setItem('dororaid:desktop-sidebar-collapsed', sidebarCollapsed ? '1' : '0'); }, [sidebarCollapsed]);
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); document.title = `${meta.title} · ${unionName} · DoroParty`; }, [meta.title, page, unionName]);

  let content = null;
  if (page === 'management/support' && !canManagePlatform) content = <DesktopFeaturePlaceholder title="플랫폼 관리자 권한이 없습니다" description="사이트 문의 관리는 DoroParty 플랫폼 관리자만 사용할 수 있습니다." />;
  else if (page.startsWith('management') && !canOpenManagement) content = <DesktopFeaturePlaceholder title="관리 권한이 없습니다" description="주딱 또는 파딱에게 관리 권한을 확인해주세요." />;
  else if (page === 'members' && !canManageUnion) content = <DesktopFeaturePlaceholder title="멤버 관리 권한이 없습니다" description="멤버 관리 화면은 운영 권한 계정만 사용할 수 있습니다." />;
  else if (page === 'raid') content = <DesktopRaidOverview canManage={canManageUnion} onNavigate={navigate} />;
  else if (page === 'control') content = <RaidControlRoom user={user} unionName={unionName} />;
  else if (page === 'practice') content = <DesktopRaidPractice canManage={canManageMemberData} />;
  else if (page === 'decks') content = <RecommendedDeckBoard canManage={canManageUnion} unionId={unionId} unionName={unionName} viewerRole={role} />;
  else if (page === 'tetris') content = <RaidStrategyPage />;
  else if (page === 'notice') content = <DesktopUnionNotices onNavigate={navigate} />;
  else if (page === 'support') content = <SiteSupportBoard />;
  else if (page === 'settlement') content = <DesktopRaidSettlement />;
  else if (page === 'members') content = <DesktopUnionMemberManagement />;
  else if (page === 'my-info' || page === 'my-nikke' || page === 'account') content = <MyInfoPage user={user} entry={entry} onSignOut={onSignOut} onNavigate={navigate} canManageUnion={canManageUnion} canManageMemberData={canManageMemberData} canOpenManagement={canOpenManagement} initialSection={page === 'account' ? 'account' : page === 'my-nikke' ? 'nikke' : 'overview'} />;
  else if (page === 'management') {
    const managementItems = [
      ...(canManageUnion ? [
        { id: 'management/season', icon: 'season', title: '차수·보스 설정', description: '레이드 차수와 보스 정보 관리' },
        { id: 'management/bridge', icon: 'bridge', title: '레이드 수집기', description: 'BlablaLink 실전 기록 직접 불러오기' },
      ] : []),
      ...(canManagePlatform ? [
        { id: 'management/support', icon: 'notice', title: '사이트 문의 관리', description: 'DoroParty 이용자 문의 확인과 답변', badge: badges.supportCount || '' },
        { id: 'management/platform', icon: 'platform', title: '플랫폼 관리', description: '유니온 등록 심사·계정 및 식별자 밴 관리', badge: badges.platformCount || '' },
      ] : []),
    ];
    content = managementItems.length ? <DesktopMenu items={managementItems} onNavigate={navigate} /> : <DesktopFeaturePlaceholder title="관리 메뉴 없음" description="현재 계정에 사용할 수 있는 관리 기능이 없습니다." />;
  } else if (page === 'management/season') content = <DesktopRaidSeason />;
  else if (page === 'management/bridge') content = <DesktopRaidBridge />;
  else if (page === 'management/support') content = <SiteSupportBoard manage />;
  else if (page === 'management/platform') content = <DesktopPlatformUnionReview />;

  const navActivePage = page.startsWith('management') ? 'management' : page;
  const captureConfig = page === 'tetris'
    ? { selector: '.raid-strategy-page', filename: () => `${unionName}-테트리스-${new Date().toISOString().slice(0, 10)}` }
    : page === 'settlement'
      ? { selector: '.raid-settlement', filename: () => `${unionName}-결산-${new Date().toISOString().slice(0, 10)}` }
      : null;

  return (
    <div className={`desktop-shell desktop-sidebar-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${controlRouteEntering ? 'control-route-entering' : ''}`.trim()}>
      <aside className="desktop-sidebar">
        <div className="desktop-sidebar-head">
          <button className="desktop-sidebar-brand" type="button" onClick={() => navigate('raid')} title="레이드 현황으로 이동"><span className="desktop-sidebar-brand-copy"><small>NIKKE UNION RAID</small><strong><DoroPartyWordmark compact /></strong><em>{unionName}</em></span></button>
          <button className="desktop-sidebar-toggle" type="button" aria-label={sidebarCollapsed ? '사이드바 펼치기' : '사이드바 접기'} aria-pressed={sidebarCollapsed} onClick={() => setSidebarCollapsed((value) => !value)}><span aria-hidden="true"><DororaidIcon name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'} size={18} /></span><strong>{sidebarCollapsed ? '펼치기' : '접기'}</strong></button>
        </div>
        <DesktopSidebarNavigation sections={navigationSections} activePage={navActivePage} onNavigate={navigate} />
        <div className="desktop-sidebar-footer"><ProfileEntryButton nickname={nickname} email={user?.email} avatarUrl={profileAvatar?.avatarUrl} onClick={() => navigate('my-info')} /><button className="desktop-logout-button" type="button" onClick={onSignOut} title="로그아웃"><span aria-hidden="true"><DororaidIcon name="logout" size={19} /></span><strong>로그아웃</strong></button></div>
      </aside>
      <div className="desktop-workspace">
        <header className="desktop-workspace-header">
          <div className="desktop-workspace-heading"><p>{unionName}</p><div><h1>{meta.title}</h1><span>{meta.description}</span></div></div>
          <div className="desktop-workspace-actions"><SupabaseStatus captureConfig={captureConfig} /><BroadcastEntryButton variant="nav" /></div>
        </header>
        <main className="desktop-main desktop-page-main desktop-integrated-page">
          {controlRouteEntering ? <div className="desktop-control-route-shield" aria-hidden="true" /> : null}
          {meta.back ? <button className="desktop-page-back" type="button" onClick={() => navigate(meta.back)}><DororaidIcon name="chevronLeft" size={17} /> <span>{PAGE_META[meta.back]?.title || '뒤로'}</span></button> : null}
          <section className="desktop-page-content integrated" key={page}>{content}</section>
        </main>
        <SiteFooter />
      </div>
    </div>
  );
}
